import { DECIDE_MSG, ESTIMATE_FILTER_LABEL, ESTIMATE_PC_MSG, LIST_MSG, TRASH_MSG, estimateFilterOf, inListPeriod, listQueryHit, type DecideResult, type EstimateFilter, type EstimateList, type EstimateSummary,
  displayDocumentNo,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, ApiError, messageOf } from "../api.js";
import { FilterInput, ListHead } from "../components/ListHead.jsx";
import { ListFilterBar, useListFilters } from "../components/ListFilters.jsx";
import { ListTabs, listTabPanel } from "../components/ListTabs.jsx";
import { RowMenu } from "../components/RowMenu.jsx";
import { usePageName } from "../components/BackButton.jsx";
import { Icon } from "../components/Shell.jsx";
import { ErrorText, useToast } from "../components/ui.jsx";
import { t, useLabels } from "../labels.js";
import { useOngoing } from "../lib/ongoing.js";
import { md, yen } from "../lib/num.js";
import { useCreateInvoice } from "./Invoices.jsx";
import { TrashTable, useTrashFlash } from "./estimates/Trash.jsx";

import { ymdFull, ymdShort } from "../lib/dates.js";
const FILTERS: EstimateFilter[] = ["all", "draft", "waiting", "ordered", "billed", "lost"];
/** #200（docs/81）：タブの最後に「ゴミ箱」（状態の絞り込みではないので「すべて」に入れない） */
type ListKey = EstimateFilter | "trash";

/** 状態の札（#302：全部の状態をチップで。受注は塗り・未受注は淡い主色・ほかは淡い地） */
export function EstimateStateChip({ st, title }: { st: EstimateFilter; title?: string | undefined }) {
  const cls = st === "ordered" ? "chip lst solid" : st === "waiting" ? "chip lst ai" : "chip lst quiet";
  return (
    <span className={cls} title={title} data-testid="row-state">
      {ESTIMATE_FILTER_LABEL[st]}
    </span>
  );
}

/**
 * 見積書の一覧（試作 v3.9 の③、docs/10 §9.2 → #302 一覧の型、Figma「一覧の見直し（11/4）」①）。押すとその紙が開く。
 *   ・列：見積日・番号・{customer}・{subject}・金額（税込）・状態・受注・請求（#302 A1）
 *   ・状態で絞るタブ：すべて／下書き／未受注／受注／請求済／失注（件数つき）。最後に「ゴミ箱 n」（#200、docs/81）
 *   ・タブの段の右に絞り込み（番号・{customer}・{subject}）、その下に期間・{customer}の絞りと絞った結果の件数・税抜・税込（#302 A6）。
 *     タブの件数も絞った結果で数える。{customer}の詳細の「見積一覧表示」は ?client= で来る
 *   ・2行目は {project}名が{subject}と違うとき「{project}：名前 ›」・分割の「分割 n／m回」・訂正版の「n回目 未発行」だけ（#302 A3）。
 *     「一括」「{project}表示 ›」は出さない。受注・失注を決めた日は状態の札に乗せたとき
 *   ・未受注の行の「受注」「失注」（確認は挟まず、トーストの「元に戻す」で戻せる。024）
 *   ・受注の行に「請求書作成」（見積の明細を写して請求書に）。下書きがあれば「請求書下書き表示」。残りの動きは行の右端の「⋯」
 */
export function EstimatesPage() {
  const L = useLabels();
  const og = useOngoing();
  usePageName("見積書");
  const nav = useNavigate();
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  const createInvoice = useCreateInvoice(toast);
  const [sp, setSp] = useSearchParams();
  const lf = useListFilters();
  const [q, setQ] = useState("");
  const fk = sp.get("f") ?? "";
  const f: ListKey = fk === "trash" ? "trash" : (FILTERS as string[]).includes(fk) ? (fk as EstimateFilter) : "all";
  const setF = (k: ListKey) =>
    setSp(
      (cur) => {
        const n = new URLSearchParams(cur);
        if (k === "all") n.delete("f");
        else n.set("f", k);
        return n;
      },
      { replace: true },
    );
  useTrashFlash();
  const list = useQuery({ queryKey: ["estimates"], queryFn: () => api<EstimateList>("GET", "/estimates") });
  // #302（A6）：{customer}・期間・絞り込みを先にかける（タブの件数も合計も絞った結果）
  const all = (list.data?.items ?? []).filter(
    (x) =>
      (!lf.client || x.clientId === lf.client) &&
      inListPeriod(x.issueDate, lf.range) &&
      listQueryHit(q, displayDocumentNo(x.documentNo, x.revisionNo), x.clientName, x.clientFullName, x.branchName, x.subject, x.groupName),
  );
  const clientName = lf.client ? (list.data?.items ?? []).find((x) => x.clientId === lf.client)?.clientName : undefined;
  const count = (k: EstimateFilter) => (k === "all" ? all.length : all.filter((x) => estimateFilterOf(x) === k).length);
  const items = f === "all" || f === "trash" ? all : all.filter((x) => estimateFilterOf(x) === f);
  const filtered = lf.active || q.trim() !== "";

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["estimates"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
    void qc.invalidateQueries({ queryKey: ["estimate"] });
  };
  const undecide = async (x: EstimateSummary) => {
    try {
      await api("POST", `/estimates/${x.id}/undecide`, {});
      toast(`${x.subject || x.clientName} を未受注に戻しました。`);
      refresh();
    } catch (e) {
      toast(e instanceof ApiError && e.code === "UNDECIDE_HAS_INVOICE" ? DECIDE_MSG.hasInvoice : messageOf(e), undefined, "error");
    }
  };
  const decide = async (x: EstimateSummary, decision: "ordered" | "lost") => {
    try {
      await api<DecideResult>("POST", `/estimates/${x.id}/decide`, { decision });
      refresh();
      // 「未受注」で絞っているときは、行が消えて見えなくならないよう、その行の新しい状態へ絞りを移す
      if (f === "waiting") setF(decision === "ordered" ? "ordered" : "lost");
      toast(decision === "ordered" ? `${x.subject || x.clientName} を受注にしました。「請求書作成」から請求書を作成できます。` : `${x.subject || x.clientName} を失注にしました。`, { label: "元に戻す", onClick: () => void undecide(x) });
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    }
  };
  const stop = { onClick: (e: React.MouseEvent) => e.stopPropagation(), onKeyDown: (e: React.KeyboardEvent) => e.stopPropagation() };

  return (
    <ListHead title="見積書">
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      <div className="lcard">
      <ListTabs<ListKey>
        id="el"
        label="見積書を状態で絞る"
        tabs={[
          ...FILTERS.map((k) => ({ key: k, label: ESTIMATE_FILTER_LABEL[k], count: list.data ? count(k) : "…" })),
          { key: "trash" as const, label: TRASH_MSG.tab, count: list.data ? (list.data.trashCount ?? 0) : "…" },
        ]}
        value={f}
        onChange={setF}
        filter={f === "trash" ? undefined : <FilterInput value={q} onChange={setQ} placeholder={t(LIST_MSG.searchDoc)} testId="el-search" />}
        create={
          <Link className="btn primary" to="/estimates/new" data-testid="estimate-new">
            <Icon k="plus" />
            見積書作成
          </Link>
        }
      />
      {f === "trash" ? null : (
        <ListFilterBar
          f={lf}
          clientName={clientName}
          sum={list.data ? { count: items.length, subtotal: items.reduce((a, x) => a + (x.subtotal ?? 0), 0), total: items.reduce((a, x) => a + x.total, 0) } : null}
        />
      )}
      <div className="tbl-wrap ltab-panel" {...listTabPanel("el", f)}>
        {f === "trash" ? <TrashTable /> : (
        <table className="list el-list" data-testid="estimate-list">
          <thead>
            <tr>
              <th>{LIST_MSG.colQuoteDate}</th>
              <th>{LIST_MSG.colNo}</th>
              <th>{L.customer}</th>
              <th>{L.subject}</th>
              <th className="amt">{LIST_MSG.colTotal}</th>
              <th>{LIST_MSG.colState}</th>
              <th>{LIST_MSG.colOrder}</th>
              <th>{LIST_MSG.colBill}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((x) => {
              const st = estimateFilterOf(x);
              const no = displayDocumentNo(x.documentNo, x.revisionNo);
              const decided = x.order.decidedAt ? LIST_MSG.decidedOn(st === "lost" ? "失注" : "受注", md(x.order.decidedAt.slice(0, 10))) : undefined;
              // 2行目（#302 A3）：{project}名が{subject}と違うとき（継続案件はいつも）
              const groupLine = x.groupName && (x.groupName !== x.subject || (og.on && x.groupIsOngoing));
              const more = [
                ...((st === "ordered" || st === "billed") && !x.order.plan && x.order.invoice && x.order.invoice.status !== "draft" ? [{ label: "請求書追加作成", onSelect: () => void createInvoice(x.id), testId: "row-invoice-more" }] : []),
                ...((st === "ordered" || st === "billed") && !x.order.plan && !x.order.invoice && x.groupBillingTiming === "split" && x.groupId ? [{ label: "一括請求", onSelect: () => void createInvoice(x.id), testId: "row-invoice-lump" }] : []),
                ...((st === "ordered" || st === "billed" || st === "lost") && x.order.canUndecide ? [{ label: "未受注に戻す", onSelect: () => void undecide(x), testId: "row-undecide" }] : []),
              ];
              return (
                <tr key={x.id} className="r" data-testid="estimate-row" onClick={() => nav(`/estimates/${x.id}`)} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && nav(`/estimates/${x.id}`)}>
                  <td className="num ldate" title={ymdFull(x.issueDate)}>{ymdShort(x.issueDate)}</td>
                  <td className="num lno">
                    {no ?? <span className="muted">{LIST_MSG.unissued}</span>}
                    {x.draftRevisionNo != null ? (
                      <span className="l2">
                        <span className="chip" title={`未発行の ${x.draftRevisionNo}回目があります`} data-testid="row-draft-rev">{x.draftRevisionNo}回目 未発行</span>
                      </span>
                    ) : null}
                  </td>
                  <td title={[x.clientFullName ?? x.clientName, x.branchName].filter(Boolean).join(" ")}>
                    <span className="lname">
                      <b>{x.clientName}</b>
                      {x.branchName ? <span className="muted"> {x.branchName}</span> : null}
                    </span>
                  </td>
                  <td>
                    {x.subject || <span className="muted">（{L.subject}はまだ）</span>}
                    {groupLine ? (
                      <span className="l2" {...stop}>
                        {x.groupId ? (
                          <Link to={`/groups/${x.groupId}`} data-testid="row-group" title={LIST_MSG.groupLine(L.project, x.groupName!)}>
                            {LIST_MSG.groupLine(L.project, x.groupName!)}
                          </Link>
                        ) : (
                          <span data-testid="row-group">{`${L.project}：${x.groupName}`}</span>
                        )}
                      </span>
                    ) : null}
                  </td>
                  <td className="amt num">{x.total ? yen(x.total) : <span className="muted">—</span>}</td>
                  <td>
                    <EstimateStateChip st={st} title={decided} />
                    {x.order.plan ? (
                      <span className="l2" data-testid="row-plan">
                        {LIST_MSG.split(x.order.plan.issuedCount, x.order.plan.count)}
                      </span>
                    ) : null}
                  </td>
                  <td className="lacts" {...stop}>
                    {st === "waiting" ? (
                      <div className="row">
                        <button type="button" className="small solid" onClick={() => void decide(x, "ordered")} aria-label={`${x.subject || x.clientName} を受注`} data-testid="row-order">受注</button>
                        <button type="button" className="small plain" onClick={() => void decide(x, "lost")} aria-label={`${x.subject || x.clientName} を失注`} data-testid="row-lost">失注</button>
                      </div>
                    ) : null}
                  </td>
                  <td className="lacts" {...stop}>
                    {/* 行の主役の動き1つは枠のボタン、残りは右端の「⋯」（#92・docs/94 B-6、components/RowMenu.tsx） */}
                    <div className="row" style={{ justifyContent: "flex-end" }}>
                      {st === "ordered" || st === "billed" ? (
                        // 042：分割の予定があるときは、次の回のボタン（案件の画面と同じ決まり）
                        x.order.plan ? (
                          x.order.plan.next == null ? (
                            <Link className="small" to={`/groups/${x.groupId ?? ""}`}>すべて請求済</Link>
                          ) : x.order.plan.next.draftInvoiceId ? (
                            <Link className="small ai" to={`/invoices/${x.order.plan.next.draftInvoiceId}`}>{x.order.plan.next.seq}回目下書き表示</Link>
                          ) : (
                            <button type="button" className="small ai" onClick={() => void createInvoice(x.id, x.order.plan!.next!.id)}>
                              {x.order.plan.next.seq}回目請求書作成{x.order.plan.next.label ? `（${x.order.plan.next.label}）` : ""}
                            </button>
                          )
                        ) : x.order.invoice && x.order.invoice.status === "draft" ? (
                          <Link className="small ai" to={`/invoices/${x.order.invoice.id}`}>請求書下書き表示</Link>
                        ) : x.order.invoice ? (
                          <Link className="small" to={`/invoices/${x.order.invoice.id}`}>請求書 {x.order.invoice.documentNo}</Link>
                        ) : x.groupBillingTiming === "split" && x.groupId ? (
                          // 042：分割の案件の受注した見積は、まず分割の予定（案件の画面で開く）。一括で請求する道は「⋯」に
                          <Link className="small ai" to={`/groups/${x.groupId}?plan=${x.id}`} data-testid="row-plan-new">分割予定作成</Link>
                        ) : (
                          <button type="button" className="small ai" onClick={() => void createInvoice(x.id)} aria-label={`${x.subject || x.clientName} 請求書作成`}>請求書作成</button>
                        )
                      ) : null}
                      {more.length ? <RowMenu label={`${x.subject || x.clientName} のほかの動き`} testId="row-more" items={more} /> : <span className="rowmore-sp" aria-hidden="true" />}
                    </div>
                  </td>
                </tr>
              );
            })}
            {list.data && !items.length ? (
              <tr className="emptyrows">
                <td colSpan={8}>{filtered ? "条件に合う見積書はありません。" : all.length ? `「${ESTIMATE_FILTER_LABEL[f]}」の見積書はありません。` : "見積書はありません。「見積書作成」から作成できます。"}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        )}
      </div>
      </div>
      {toastNode}
    </ListHead>
  );
}
